class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static hideLoading() {
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer) {
      const collectionEl = gridContainer.querySelector('.collection');
      if (collectionEl) collectionEl.classList.remove('loading');
    }
    if (countContainer) {
      countContainer.classList.remove('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const updateEvent = FacetFiltersForm.startUpdateEvent(searchParams);
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));

    const gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer) {
      const collectionEl = gridContainer.querySelector('.collection');
      if (collectionEl) collectionEl.classList.add('loading');
    }
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      if (!section.section) return;
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, updateEvent)
        : FacetFiltersForm.renderSectionFromFetch(url, event, updateEvent);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static startUpdateEvent(searchParams) {
    const { SearchUpdateEvent, CollectionUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

    const facetsForm = document.querySelector('facet-filters-form');
    const facetsContainer = document.querySelector('.facets-container');
    const collectionId = facetsContainer?.dataset.collectionId || null;
    const collectionHandle = facetsContainer?.dataset.collectionHandle || '';
    const currentCount = parseInt((document.getElementById('ProductCount') || document.getElementById('ProductCountDesktop'))?.dataset.productCount) || 0;
    const urlSearchParams = new URLSearchParams(searchParams);
    const isSearchPage = facetsContainer?.dataset.template === 'search';
    const isCollectionPage = facetsContainer?.dataset.template === 'collection';
    const dispatchTarget = facetsForm || document;

    let deferred;

    if (isSearchPage && SearchUpdateEvent) {
      deferred = SearchUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query: urlSearchParams.get('q') || '',
            productFilters: SearchUpdateEvent.parseProductFilters(urlSearchParams),
            sortKey: SearchUpdateEvent.getSortKey(urlSearchParams),
          },
          promise: deferred.promise,
        }),
      );
    } else if (isCollectionPage && CollectionUpdateEvent) {
      deferred = CollectionUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new CollectionUpdateEvent({
          collection: { id: collectionId, handle: collectionHandle, productsCount: currentCount },
          productFilters: CollectionUpdateEvent.parseProductFilters(urlSearchParams),
          sortKey: CollectionUpdateEvent.getSortKey(urlSearchParams),
          promise: deferred.promise,
        }),
      );
    }

    if (!deferred) return;

    return {
      resolve: (filteredCount) => {
        deferred.resolve({ [isSearchPage ? 'totalCount' : 'productsCount']: filteredCount });
      },
      reject: deferred.reject,
    };
  }

  static renderSectionFromFetch(url, event, updateEvent) {
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderSection(html, event, updateEvent);
      })
      .catch((error) => {
        console.error('Facet fetch error:', error);
        FacetFiltersForm.hideLoading();
        updateEvent?.reject(error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, updateEvent) {
    try {
      const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
      FacetFiltersForm.renderSection(html, event, updateEvent);
    } catch (error) {
      console.error('Facet cache error:', error);
      FacetFiltersForm.hideLoading();
      updateEvent?.reject(error);
    }
  }

  static renderSection(html, event, updateEvent) {
    try {
      FacetFiltersForm.renderFilters(html, event);
      FacetFiltersForm.renderProductGridContainer(html);
      FacetFiltersForm.renderProductCount(html, updateEvent);
      if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html);
    } catch (error) {
      console.error('Facet section render error:', error);
      FacetFiltersForm.hideLoading();
      updateEvent?.reject(error);
    }
  }

  static renderProductGridContainer(html) {
    const gridContainer = document.getElementById('ProductGridContainer');
    if (!gridContainer) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const newProductGrid = parsed.getElementById('ProductGridContainer');
    if (newProductGrid) {
      gridContainer.innerHTML = newProductGrid.innerHTML;
      gridContainer.querySelectorAll('.scroll-trigger').forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
    }
  }

  static renderProductCount(html, updateEvent) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHtml.getElementById('ProductCount') || parsedHtml.getElementById('ProductCountDesktop');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (sourceCount) {
      const count = sourceCount.innerHTML;
      if (container) {
        container.innerHTML = count;
        container.dataset.productCount = sourceCount.dataset.productCount || '';
        container.dataset.totalCount = sourceCount.dataset.totalCount || '';
      }
      if (containerDesktop) {
        containerDesktop.innerHTML = count;
        containerDesktop.dataset.productCount = sourceCount.dataset.productCount || '';
        containerDesktop.dataset.totalCount = sourceCount.dataset.totalCount || '';
      }
      updateEvent?.resolve(parseInt(sourceCount.dataset.productCount) || 0);
    }
    FacetFiltersForm.hideLoading();
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );

    // Do not remove facet sections if omitted by server response (e.g. 0 matching products); mark options as disabled
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      const fetchedEl = Array.from(facetDetailsElementsFromFetch).find(({ id }) => currentElement.id === id);
      if (!fetchedEl) {
        currentElement.querySelectorAll('.facet-checkbox, .facets__label').forEach((label) => {
          label.classList.add('disabled');
        });
        currentElement.querySelectorAll('input[type="checkbox"]').forEach((inp) => {
          if (!inp.checked) inp.disabled = true;
        });
        currentElement.querySelectorAll('[class^="count--"]').forEach((span) => {
          span.textContent = ' (0)';
        });
      }
    });

    const matchesId = (element) => {
      const jsFilter = (event && event.target) ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        const oldList = currentElement.querySelector('.facets__list, .facets-layout-list');
        const newList = elementToRender.querySelector('.facets__list, .facets-layout-list');

        if (oldList && newList) {
          const oldItemsMap = new Map();
          oldList.querySelectorAll('input[type="checkbox"]').forEach((inp) => {
            const li = inp.closest('li');
            if (li && inp.value) oldItemsMap.set(inp.value, li.cloneNode(true));
          });

          currentElement.innerHTML = elementToRender.innerHTML;

          const updatedList = currentElement.querySelector('.facets__list, .facets-layout-list');
          if (updatedList && oldItemsMap.size > 0) {
            const currentValues = new Set(
              Array.from(updatedList.querySelectorAll('input[type="checkbox"]')).map((i) => i.value)
            );

            oldItemsMap.forEach((clonedLi, val) => {
              if (!currentValues.has(val)) {
                const label = clonedLi.querySelector('.facet-checkbox, .facets__label');
                if (label) label.classList.add('disabled');
                const inp = clonedLi.querySelector('input[type="checkbox"]');
                if (inp) {
                  inp.checked = false;
                  inp.disabled = true;
                }
                const countSpan = clonedLi.querySelector('[class^="count--"]');
                if (countSpan) countSpan.textContent = ' (0)';
                updatedList.appendChild(clonedLi);
              }
            });
          }
        } else {
          currentElement.innerHTML = elementToRender.innerHTML;
        }
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            const prevEl = document.getElementById(previousElementId);
            if (prevEl) prevEl.after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          const parentEl = document.querySelector(`#${elementToRender.parentElement.id} .js-filter`);
          if (parentEl) parentEl.before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender && event && event.target) {
      const targetJsFilter = event.target.closest('.js-filter');
      if (targetJsFilter && targetJsFilter.id) {
        FacetFiltersForm.renderCounts(countsToRender, targetJsFilter);
        const mobileTarget = document.getElementById(targetJsFilter.id);
        if (mobileTarget) {
          FacetFiltersForm.renderMobileCounts(countsToRender, mobileTarget);
        }

        const newFacetDetailsElement = document.getElementById(targetJsFilter.id);
        const isTextInput = event.target.getAttribute && event.target.getAttribute('type') === 'text';

        if (!isTextInput && newFacetDetailsElement) {
          const originatingInputId = event.target.id;
          const matchingInput = originatingInputId
            ? newFacetDetailsElement.querySelector(`#${CSS.escape(originatingInputId)}`)
            : null;

          if (matchingInput) {
            matchingInput.focus();
          }
        }
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const targetElement = document.querySelector(selector);
      if (!activeFacetsElement || !targetElement) return;
      targetElement.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const sourceEl = html.querySelector(selector);
      const targetEl = document.querySelector(selector);
      if (!sourceEl || !targetEl) return;
      targetEl.innerHTML = sourceEl.innerHTML;
    });

    const mobileFormDrawer = document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer');
    if (mobileFormDrawer && typeof mobileFormDrawer.bindEvents === 'function') {
      mobileFormDrawer.bindEvents();
    }
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams ? '?'.concat(searchParams) : ''}`);
  }

  static getSections() {
    const productGrid = document.getElementById('product-grid');
    return [
      {
        section: productGrid ? productGrid.dataset.id : '',
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData);

    // Remove price slider parameters if they are at default values (min = 0, max = maxRange)
    const priceSliders = form.querySelectorAll('.price-slider-visual-container');
    priceSliders.forEach((container) => {
      const minSlider = container.querySelector('.min-slider');
      const maxSlider = container.querySelector('.max-slider');
      const maxRange = parseFloat(container.dataset.maxRange);

      if (minSlider) {
        const minVal = parseFloat(minSlider.value);
        if (isNaN(minVal) || minVal === 0) {
          searchParams.delete(minSlider.name);
        }
      }
      if (maxSlider) {
        const maxVal = parseFloat(maxSlider.value);
        if (isNaN(maxVal) || (maxRange && maxVal >= maxRange)) {
          searchParams.delete(maxSlider.name);
        }
      }
    });

    const priceRangeEls = form.querySelectorAll('price-range');
    priceRangeEls.forEach((priceRange) => {
      const inputs = priceRange.querySelectorAll('input');
      if (inputs.length >= 2) {
        const minInput = inputs[0];
        const maxInput = inputs[1];
        const minVal = parseFloat(minInput.value);
        const maxVal = parseFloat(maxInput.value);
        const maxRange = parseFloat(minInput.getAttribute('data-max') || maxInput.getAttribute('max') || maxInput.getAttribute('data-max'));

        if (minInput.name && (isNaN(minVal) || minVal === 0)) {
          searchParams.delete(minInput.name);
        }
        if (maxInput.name && (isNaN(maxVal) || (maxRange && maxVal >= maxRange))) {
          searchParams.delete(maxInput.name);
        }
      }
    });

    return searchParams.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    if (event) event.preventDefault();

    // Check if mobile filter popup is open
    const filtersAside = document.getElementById('main-collection-filters');
    const isMobilePopupOpen = filtersAside && filtersAside.classList.contains('is-open');

    // If mobile filter popup is open and this is an input event (automatic debounce), skip auto-submit
    if (isMobilePopupOpen && event && event.type === 'input' && !event.detail?.isApply) {
      return;
    }

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const targetForm = event && event.target ? event.target.closest('form') : null;

    if (event && event.srcElement && event.srcElement.className === 'mobile-facets__checkbox' && targetForm) {
      const searchParams = this.createSearchParams(targetForm);
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = targetForm && targetForm.id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    if (event) event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    let href = '';
    if (event) {
      const targetAnchor = event.currentTarget || (event.target ? event.target.closest('a') : null);
      if (targetAnchor) {
        href = targetAnchor.getAttribute('href') || targetAnchor.href || '';
      }
    }
    const url = !href || href.indexOf('?') === -1 ? '' : href.slice(href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    if (inputs.length < 2) return;
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    if (!input) return;
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initFacetLink();
  }

  initFacetLink() {
    const facetLink = this.querySelector('a');
    if (facetLink && !facetLink.dataset.hasFacetRemoveListener) {
      facetLink.dataset.hasFacetRemoveListener = 'true';
      facetLink.setAttribute('role', 'button');
      facetLink.addEventListener('click', this.closeFilter.bind(this));
      facetLink.addEventListener('keyup', (event) => {
        event.preventDefault();
        if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
      });
    }
  }

  closeFilter(event) {
    if (event) event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    const isMobilePopup = this.closest('.mobile-facets__popup-footer, #main-collection-filters, #main-search-filters, .facets-wrapper');

    if (isMobilePopup && form) {
      const filterForm = form.querySelector('form') || form;
      // Uncheck all checkboxes in the popup
      filterForm.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
        cb.checked = false;
      });
      // Reset price range sliders & badges to default min (0) and max (maxRange)
      filterForm.querySelectorAll('.price-slider-visual-container').forEach((container) => {
        const minSlider = container.querySelector('.min-slider');
        const maxSlider = container.querySelector('.max-slider');
        const maxRange = parseFloat(container.dataset.maxRange) || 1000;
        if (minSlider) minSlider.value = 0;
        if (maxSlider) maxSlider.value = maxRange;
        minSlider?.dispatchEvent(new Event('input', { bubbles: true }));
        maxSlider?.dispatchEvent(new Event('input', { bubbles: true }));
      });
      filterForm.querySelectorAll('price-range input').forEach((inp) => {
        const maxAttr = parseFloat(inp.getAttribute('data-max') || inp.getAttribute('max'));
        if (inp.classList.contains('min-slider') || inp.id.includes('GTE')) {
          inp.value = 0;
        } else if (!isNaN(maxAttr)) {
          inp.value = maxAttr;
        } else {
          inp.value = '';
        }
        inp.dispatchEvent(new Event('input', { bubbles: true }));
      });
      return;
    }

    if (form) {
      form.onActiveFilterClick(event);
    }
  }
}

customElements.define('facet-remove', FacetRemove);

// Mobile Filter Popup Event Handlers (<768px)
(function initMobileFilterPopup() {
  function getFiltersAside() {
    return document.getElementById('main-collection-filters') || document.getElementById('main-search-filters') || document.querySelector('.facets-wrapper');
  }

  function openPopup() {
    const filtersAside = getFiltersAside();
    if (filtersAside) {
      filtersAside.classList.add('is-open');
      document.body.classList.add('mobile-filter-open');
    }
  }

  function closePopup() {
    const filtersAside = getFiltersAside();
    if (filtersAside) {
      filtersAside.classList.remove('is-open');
      document.body.classList.remove('mobile-filter-open');
    }
  }

  document.addEventListener('click', function(e) {
    if (e.target.closest('#MobileFilterTriggerBtn')) {
      e.preventDefault();
      openPopup();
    } else if (e.target.closest('#MobileFilterCloseBtn')) {
      e.preventDefault();
      closePopup();
    } else if (e.target.closest('#MobileFilterApplyBtn')) {
      e.preventDefault();
      const facetFormEl = document.querySelector('facet-filters-form');
      const facetForm = document.getElementById('FacetFiltersForm');
      if (facetForm && facetFormEl) {
        const searchParams = facetFormEl.createSearchParams(facetForm);
        FacetFiltersForm.renderPage(searchParams);
      }
      closePopup();
    } else {
      const filtersAside = getFiltersAside();
      if (filtersAside && filtersAside.classList.contains('is-open') && e.target === filtersAside) {
        closePopup();
      }
    }
  });

  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') {
      const filtersAside = getFiltersAside();
      if (filtersAside && filtersAside.classList.contains('is-open')) {
        closePopup();
      }
    }
  });
})();

// Global Dual Price Range Slider Event Handler (Real-time badge & track fill updates)
(function initPriceSliderGlobal() {
  function updatePriceSlider(sliderInput) {
    const wrapper = sliderInput.closest('.price-facet-wrapper') || sliderInput.closest('.price-range-custom-container')?.parentElement;
    const container = sliderInput.closest('.price-range-custom-container') || sliderInput.closest('.price-slider-visual-container');
    const parentContainer = sliderInput.closest('.price-range-custom-container') || wrapper || container;
    if (!parentContainer) return;

    const minSlider = parentContainer.querySelector('.min-slider');
    const maxSlider = parentContainer.querySelector('.max-slider');
    const fill = parentContainer.querySelector('#price-slider-fill') || parentContainer.querySelector('.price-slider__track-fill');
    const minBadge = parentContainer.querySelector('.price-min-badge') || parentContainer.querySelector('#price-min-badge');
    const maxBadge = parentContainer.querySelector('.price-max-badge') || parentContainer.querySelector('#price-max-badge');
    const visualContainer = parentContainer.querySelector('.price-slider-visual-container') || parentContainer;
    const maxRange = parseFloat(visualContainer?.dataset?.maxRange) || 1000;

    if (!minSlider || !maxSlider) return;

    let minVal = parseFloat(minSlider.value) || 0;
    let maxVal = parseFloat(maxSlider.value) || maxRange;

    if (minVal > maxVal) {
      if (sliderInput === minSlider) {
        minSlider.value = maxVal;
        minVal = maxVal;
      } else if (sliderInput === maxSlider) {
        maxSlider.value = minVal;
        maxVal = minVal;
      }
    }

    const leftPercent = Math.max(0, Math.min(100, (minVal / maxRange) * 100));
    const rightPercent = Math.max(0, Math.min(100, (maxVal / maxRange) * 100));

    if (fill) {
      fill.style.left = leftPercent + '%';
      fill.style.width = Math.max(0, rightPercent - leftPercent) + '%';
    }

    if (minBadge) minBadge.textContent = minVal.toFixed(2);
    if (maxBadge) maxBadge.textContent = maxVal.toFixed(2);
  }

  document.addEventListener('input', function(e) {
    const sliderInput = e.target.closest('.price-range-slider-input');
    if (sliderInput) {
      updatePriceSlider(sliderInput);
    }
  });

  document.addEventListener('pointerdown', function(e) {
    const sliderInput = e.target.closest('.price-range-slider-input');
    if (!sliderInput) return;

    const parentContainer = sliderInput.closest('.price-range-custom-container') || sliderInput.closest('.price-facet-wrapper');
    if (!parentContainer) return;

    const minSlider = parentContainer.querySelector('.min-slider');
    const maxSlider = parentContainer.querySelector('.max-slider');

    if (sliderInput === minSlider && maxSlider) {
      minSlider.style.zIndex = '5';
      maxSlider.style.zIndex = '4';
    } else if (sliderInput === maxSlider && minSlider) {
      maxSlider.style.zIndex = '5';
      minSlider.style.zIndex = '4';
    }
  });
})();


